import React, { useCallback, useEffect, useState } from 'react';

import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Chip from '@material-ui/core/Chip';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import { green, amber, orange, red, blue, grey } from '@material-ui/core/colors';

import CloudDownloadOutlinedIcon from '@material-ui/icons/CloudDownloadOutlined';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';

import { ButtonPrimary, ButtonDefault } from '../../components/Button/Button';
import { ToastCF } from '../../components/Toast/Toast';

import CandidatoService from '../../services/Candidato';
import ProvaService from '../../services/Prova';
import EnumService from '../../services/Enum';
import { descreverErro } from '../../services/API';
import { download } from '../../helper/FileHelper';
import { getDataDaDivulgacaoDeNotasFormatado } from '../../helper/ContextHelper';
import { patternEnum } from '../../variables/Enums/Enum';

const NOTA_DISPONIVEL = 'NOTA_DISPONIVEL';
const EM_RECURSO = 'EM_RECURSO';
const DESCARTADA = 'DESCARTADA';
const AGUARDANDO_DIVULGACAO = 'AGUARDANDO_DIVULGACAO';
const SEM_PROVA = 'SEM_PROVA';

const COR_SITUACAO = {
    NOTA_DISPONIVEL: green[600],
    EM_RECURSO: amber[800],
    DESCARTADA: red[600],
    EM_ANALISE: orange[700],
    AGUARDANDO_DIVULGACAO: blue[500],
    SEM_PROVA: grey[600],
};

const useStyles = makeStyles(theme => ({
    paper: {
        padding: 24,
        borderRadius: 12,
        border: '1px solid #e9ecef',
    },
    titulo: {
        fontWeight: 500,
        marginBottom: 12,
    },
    carregando: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        padding: theme.spacing(2, 0),
    },
    cabecalho: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: theme.spacing(1.5),
        marginBottom: theme.spacing(2),
    },
    codigo: {
        marginLeft: 'auto',
    },
    escritos: {
        padding: 16,
        borderRadius: 12,
        backgroundColor: '#f2f6ff',
        border: '1px solid #d6e0ff',
        marginBottom: theme.spacing(2),
    },
    notaBox: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        padding: theme.spacing(1, 0),
        marginBottom: theme.spacing(1),
    },
    nota: {
        fontWeight: 700,
        fontSize: 40,
        lineHeight: 1,
        color: green[700],
    },
    notaRecurso: {
        color: amber[900],
    },
    mensagem: {
        color: grey[800],
        lineHeight: 1.6,
    },
    recurso: {
        marginTop: theme.spacing(3),
        padding: 16,
        borderRadius: 12,
        backgroundColor: '#fafafa',
        border: '1px dashed #cfcfcf',
    },
    recursoTitulo: {
        fontWeight: 600,
        marginBottom: 6,
    },
    recursoAcao: {
        marginTop: theme.spacing(1),
    },
}));

const MENSAGEM_ERRO_PADRAO = 'Não foi possível carregar o resultado da prova.';

const PainelProva = ({ candidatoId, contexts }) => {
    const classes = useStyles();

    const [carregando, setCarregando] = useState(true);
    const [prova, setProva] = useState(null);
    const [opcoesSituacao, setOpcoesSituacao] = useState([]);
    const [baixando, setBaixando] = useState(false);
    const [toast, setToast] = useState({ open: false, message: '', type: 'error' });

    const notificar = useCallback((message, type = 'error') => setToast({ open: true, message, type }), []);

    useEffect(() => {
        let ativo = true;

        const carregar = async () => {
            setCarregando(true);

            try {
                const [respostaProva, respostaEnum] = await Promise.all([
                    CandidatoService.carregarProva(candidatoId),
                    EnumService.listarSituacoesProvaCandidato(),
                ]);

                if (!ativo) return;

                setProva(respostaProva.status === 204 ? null : respostaProva.data);
                setOpcoesSituacao(respostaEnum.data || []);
            } catch (error) {
                if (ativo) notificar(descreverErro(error, MENSAGEM_ERRO_PADRAO));
            } finally {
                if (ativo) setCarregando(false);
            }
        };

        if (candidatoId) carregar();

        return () => { ativo = false; };
    }, [candidatoId, notificar]);

    const baixarCartao = () => {
        if (!prova || !prova.id) return;

        setBaixando(true);

        ProvaService.downloadCartao(prova.id)
            .then(response => download(response))
            .catch(error => notificar(descreverErro(error, MENSAGEM_ERRO_PADRAO)))
            .finally(() => setBaixando(false));
    };

    const situacao = prova && prova.situacao ? prova.situacao : SEM_PROVA;
    const situacaoLabel = patternEnum(situacao, opcoesSituacao);
    const dataDivulgacao = contexts ? getDataDaDivulgacaoDeNotasFormatado(contexts) : null;
    const cartaoDisponivel = Boolean(prova && prova.id && prova.cartaoDisponivel);

    const renderConteudo = () => {
        if (situacao === NOTA_DISPONIVEL && prova && prova.notaFinal != null)
            return (
                <div className={classes.notaBox}>
                    <Typography variant="body1" color="textSecondary">Nota Final</Typography>
                    <Typography className={classes.nota}>{prova.notaFinal}</Typography>
                </div>
            );

        if (situacao === EM_RECURSO)
            return (
                <>
                    <div className={classes.notaBox}>
                        <Typography variant="body1" color="textSecondary">Nota Pré-Recurso</Typography>
                        <Typography className={`${classes.nota} ${classes.notaRecurso}`}>
                            {prova && prova.notaPreRecurso != null ? prova.notaPreRecurso : '--'}
                        </Typography>
                    </div>
                    <Typography variant="body2" display="block" className={classes.mensagem}>
                        A nota acima ainda pode ser alterada após a análise do recurso.
                    </Typography>
                </>
            );

        if (situacao === DESCARTADA || situacao === SEM_PROVA)
            return (
                <Typography variant="body2" display="block" className={classes.mensagem}>
                    Não localizamos uma prova vinculada ao seu cadastro. Se você fez a prova e preencheu um cartão-resposta, procure a Central da OBMEP para que possamos verificar o ocorrido.
                </Typography>
            );

        if (situacao === AGUARDANDO_DIVULGACAO)
            return (
                <Typography variant="body2" display="block" className={classes.mensagem}>
                    {dataDivulgacao ? `As notas serão divulgadas em ${dataDivulgacao} (Horário de Brasília).` : 'As notas serão divulgadas em breve.'}
                </Typography>
            );

        return (
            <Typography variant="body2" display="block" className={classes.mensagem}>
                Sua prova está em análise. A nota será disponibilizada assim que o processo for concluído.
            </Typography>
        );
    };

    return (
        <Paper elevation={3} className={classes.paper}>
            <ToastCF
                open={toast.open}
                message={toast.message}
                type={toast.type}
                handleClose={() => setToast(anterior => ({ ...anterior, open: false }))}
            />

            <Typography variant="h6" className={classes.titulo}>Resultado da Prova</Typography>

            {carregando ?
                <div className={classes.carregando}>
                    <CircularProgress size={22} />
                    <Typography variant="body2" color="textSecondary">Carregando o resultado da sua prova...</Typography>
                </div>
                :
                <>
                    <div className={classes.cabecalho}>
                        <Chip
                            label={situacaoLabel}
                            style={{ backgroundColor: COR_SITUACAO[situacao] || grey[600], color: '#fff', fontWeight: 600 }}
                        />
                        {prova && prova.codigo &&
                            <Typography variant="body2" color="textSecondary" className={classes.codigo}>
                                Código da Prova: <b>{prova.codigo}</b>
                            </Typography>
                        }
                    </div>

                    {prova && (prova.nomeEscrito || prova.cpfEscrito) &&
                        <Paper elevation={0} className={classes.escritos}>
                            <Typography variant="body2" color="textSecondary" display="block">
                                Dados escritos no cartão da prova:
                            </Typography>
                            {prova.nomeEscrito &&
                                <Typography variant="body2" display="block">Nome: <b>{prova.nomeEscrito}</b></Typography>
                            }
                            {prova.cpfEscrito &&
                                <Typography variant="body2" display="block">CPF: <b>{prova.cpfEscrito}</b></Typography>
                            }
                        </Paper>
                    }

                    {renderConteudo()}

                    <Grid container spacing={2} style={{ marginTop: 8 }}>
                        <Grid item xs={12} sm={6}>
                            <ButtonPrimary
                                startIcon={<CloudDownloadOutlinedIcon />}
                                onClick={baixarCartao}
                                disabled={!cartaoDisponivel || baixando}
                                name={baixando ? 'Baixando...' : 'Baixar Meu Cartão-Resposta da Prova'}
                            />
                            {prova && prova.id && !prova.cartaoDisponivel &&
                                <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 6 }}>
                                    O cartão-resposta da sua prova ainda não está disponível para download.
                                </Typography>
                            }
                        </Grid>
                    </Grid>

                    {prova && prova.id &&
                        <Paper elevation={0} className={classes.recurso}>
                            <Typography variant="body2" className={classes.recursoTitulo}>Petição de Recurso de Nota</Typography>
                            <Typography variant="body2" color="textSecondary" display="block">
                                Em breve você poderá registrar aqui a sua petição de recurso sobre a nota desta prova.
                            </Typography>
                            <div className={classes.recursoAcao}>
                                <ButtonDefault
                                    startIcon={<LockOutlinedIcon />}
                                    name="Petição de Recurso — Em Breve"
                                    disabled
                                />
                            </div>
                        </Paper>
                    }
                </>
            }
        </Paper>
    );
};

export default PainelProva;
